import { BOOSTER_LIST, GameItemConfig, ItemIDs } from '../../Config/Items';
import { SHOP_GIFT_TIME } from '../../Config/Shop';
import GameLoader from '../../Core/Assets';
import Core, { GameOrientation } from '../../Core/Core';
import { Platform } from '../../SDK/SDK';
import { IXPromoConfig } from '../../SDK/SDKsg';
import { Actions } from '../../State/Actions/Actions';
import { IStateBoard } from '../../State/reducers/board';
import { IStateBoosters } from '../../State/reducers/boosters';
import { IStateCurrency } from '../../State/reducers/currency';
import { IStateMissions, MissionState } from '../../State/reducers/missions';
import { IStatePlayer } from '../../State/reducers/player';
import { checkForNotification } from '../../State/reducers/social';
import { Views } from '../../State/reducers/view';
import { IGameState } from '../../State/Store/store';
import Tile from '../../Tiles/Tile';
import { BottomUi } from '../../UI/Game/BottomUI/BottomUi';
import { TopUi } from '../../UI/Game/TopUI/TopUi';
import { BottomMapUi } from '../../UI/Map/BottomMapUi';
import { COOLDOWNPRIZEWHEEL } from '../../UI/Map/PrizeWheelPopup';
import TopMapUi from '../../UI/Map/TopMapUi';
import { COOLDOWN } from '../../UI/PopUps/Social/GiftFriendTab';
import CornerButton from '../../UI/Shared/Buttons/CornerButton';
import { PrizeWheelButton } from '../../UI/Shared/Buttons/PrizeWheelButton';
import RoundedIconButton from '../../UI/Shared/Buttons/RoundedIconButton';
import { EmbeddedSprite } from '../../UI/Shared/EmbeddedSprite';
import DateUtils from '../../Utils/DateUtils';
import { getIsIOS } from '../../Utils/Platform';
import HudView from '../../View/HudView';
import { Manager } from '../Manager';
import { Popup } from '../Popups';
import { XPromoTracking } from '../Tracking/XPromoTracking';
import { HudActions, HudEmitter } from './HudEmitter';

export class Ui extends Manager {
    public bottomUi: BottomUi;
    public mapUi: TopMapUi;
    public mapBottomUi: BottomMapUi;
    public topUi: TopUi;
    public botButton: CornerButton = null;
    public shortcutButton: CornerButton = null;
    public prizeWheelButton: PrizeWheelButton;
    public freeBoosterList: string[];
    private view: HudView;
    private modalAction: (confirm: boolean) => void;

    constructor(view: HudView) {
        super();
        this.view = view;

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.handleCurrencyChange(state, pre);
        });

        this.addWatcher(this.addGameUi, state => state.StateBoard.ready);

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.handleScoreChange(state, pre);
        });

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.levelComplete(state, pre);
        });

        this.addSelector(
            (state: IStateBoosters, pre: IStateBoosters) => {
                this.handleBoosterChange(state, pre);
            },
            (state: IGameState) => state.StateBoosters
        );

        this.addSelector(
            (state: IStateMissions, pre: IStateMissions) => {
                this.handleMissionChange(state, pre);
            },
            (state: IGameState) => state.StateMissions
        );

        this.addSelector(
            (state: IStateBoard, pre: IStateBoard) => {
                this.handlePossibleMovesChange(state, pre);
            },
            (state: IGameState) => state.StateBoard
        );

        this.addSelector(
            (state: IStatePlayer, pre: IStatePlayer) => {
                this.handleShopOpened(state, pre);
            },
            (state: IGameState) => state.StatePlayer
        );

        this.addSelector(
            (state: IGameState, pre: IGameState) => {
                this.handleSettingsChange(state, pre);
            },
            (state: IGameState) => state
        );

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.handleViewChange(state, pre);
        });

        // TODO create way to pass action to be dispatched with confirm
        this.modalAction = (confirm: boolean, action?: HudActions) => {
            if (confirm && action) {
                HudEmitter.instance.emit(action);
            }

            HudEmitter.instance.emit(HudActions.CLOSE_POPUP, Popup.MODAL);
        };

        HudEmitter.instance.on(HudActions.CLOSE_POPUP, this.handlePopupClosed.bind(this));
    }

    public showPlayerInfo(player: IStatePlayer, currency: IStateCurrency) {
        this.mapUi.showAllStats(player, currency);
    }

    public addGameUi(ready: boolean = true) {
        if (!ready) {
            return;
        }
        if (!this.topUi) {
            this.topUi = this.view.createTopUi(Core.store.getState().StateLevel.winTime);
        }

        const state = Core.store.getState();
        const stateLevel = state.StateLevel;
        const stateScore = state.StateScore;
        const tiers = stateScore.tiers.sort((a, b) => a - b);
        const maxScore = stateLevel.timed ? stateLevel.winTime : tiers[2];
        const shuffleIndex = BOOSTER_LIST.indexOf(ItemIDs.SHUFFLE);
        const targetIndex = shuffleIndex === -1 ? BOOSTER_LIST.indexOf(ItemIDs.TIME) : shuffleIndex;
        BOOSTER_LIST[targetIndex] = stateLevel.timed ? ItemIDs.TIME : ItemIDs.SHUFFLE;
        this.topUi.initTopUi(
            stateLevel.goal.type,
            stateLevel.goal.target,
            tiers,
            maxScore,
            state.StateLevel.timed,
            state.StatePlayer.activeCollection,
            state.StateScore.score
        );
        if (!this.bottomUi) {
            this.bottomUi = this.view.createBottomUi(
                Core.store.getState().StateBoosters.currentBoosters
            );
        }
        const curAvailableBoosters = Core.store.getState().StateBoosters.availableBoosters;
        this.bottomUi.showBoosters(
            curAvailableBoosters,
            Core.store.getState().StateBoosters.freeBoosters
        );
        state.StateBoosters.lockedBoosters.forEach(l =>
            this.bottomUi.boosters.lockBooster(l, true)
        );

        this.addPauseBtn();
        this.showPossibleMoves(state.StateBoard.availableMatches);
    }

    public unlockBoosters(unlockIds: ItemIDs[], free: boolean = false) {
        unlockIds.forEach(id => {
            Core.store.dispatch(Actions.unlockBooster(id));

            if (free) {
                Core.store.dispatch(Actions.addFreeBooster(id));
            }

            this.bottomUi.boosters.lockBooster(id, false);
        });

        const { StateBoosters } = Core.store.getState();
        const curAvailableBoosters = StateBoosters.availableBoosters;

        if (!this.bottomUi) {
            return;
        }

        this.bottomUi.showBoosters(curAvailableBoosters, StateBoosters.freeBoosters);
        StateBoosters.lockedBoosters.forEach(l => this.bottomUi.boosters.lockBooster(l, true));
    }

    public async addMapUi() {
        await GameLoader.loadAssetList({
            assetList: ['mapui', 'commonui'],
            onProgress: null,
            cb: null,
            oncomplete: () => {},
        });

        this.mapUi = this.view.createMapUi();
        this.mapBottomUi = this.view.createMapBottomUi();
        if (Core.sdk.payments.enabled) {
            const shopButton = this.mapBottomUi.addButton(
                'mapui/navi-icon-shop',
                Popup.SHOP,
                Core.orientation === GameOrientation.PORTRAIT ? 1 : 1.2
            );
            shopButton.appear();
            this.addShopGift(shopButton);
        }

        const socialButton = this.mapBottomUi.addButton('mapui/navi-icon-social', Popup.SOCIAL);
        if (sgPlatform !== Platform.Network) {
            socialButton.appear();
        }

        const journalButton = this.mapBottomUi.addButton('mapui/navi-icon-journal', Popup.JOURNAL);
        journalButton.appear();

        this.setSocialNotifications();

        const mapgateButton = this.mapBottomUi.addButton('mapui/navi-icon-world', Popup.MAPGATE);
        mapgateButton.appear();
        if (Core.store.getState().StatePlayer.lastLevelIndex >= 10) {
            if (!Core.store.getState().StateMissions.missionsShown) {
                journalButton.callAttention();
            }
            const completedMissions = Core.store
                .getState()
                .StateMissions.missions.filter(m => m.missionState === MissionState.COMPLETE);

            if (completedMissions.length > 0) {
                journalButton.showNotification();
            }
        }
        journalButton.enabled = journalButton.interactive =
            Core.store.getState().StatePlayer.lastLevelIndex >= 10;

        mapgateButton.enabled = mapgateButton.interactive =
            Core.store.getState().StatePlayer.unlocked.length > 1;
    }

    public removeUi(ids: string[]) {
        ids.forEach(id => {
            let ui = null;
            if (id === 'game-top') {
                ui = this.topUi;
                this.topUi = null;
            }
            if (id === 'game-bottom') {
                ui = this.bottomUi;
                this.bottomUi = null;
            }
            if (id === 'map-top') {
                ui = this.mapUi;
                this.mapUi = null;
            }

            if (id === 'map-bottom') {
                ui = this.mapBottomUi;
                this.mapBottomUi = null;
            }
            if (ui) {
                this.view.deleteUi(ui);
            }
        });
    }

    public addPauseBtn() {
        this.view.showPauseBtn();
        const state = Core.store.getState();
        const { music, soundFx: sfx } = state.StateSound;
        const view = state.StateView;
        this.view.updatePauseGroup({ music, sfx, view });

        if (view === Views.GAME) {
            HudEmitter.instance.on(HudActions.MODAL, this.modalAction);
        }
    }

    public addGiveUpBtn() {
        return this.view.showGiveUpBtn();
    }

    public hideGiveUpBtn() {
        return this.view.hideGiveUpBtn();
    }

    public hidePauseBtn() {
        this.view.hidePauseBtn();
    }

    public removePause() {
        this.view.deletePause();
        HudEmitter.instance.off(HudActions.MODAL, this.modalAction);
    }

    public addPrizeWheelBtn() {
        const timeRemaining = DateUtils.timeRemainingTo(
            COOLDOWNPRIZEWHEEL + Core.store.getState().StatePlayer.prizeWheelCooldown
        );
        this.prizeWheelButton = this.view.addPrizeWheelButton(timeRemaining < 0, () => {
            HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.PRIZEWHEEL);
        });
    }

    public removePrizeWheelBtn() {
        if (this.prizeWheelButton) {
            this.prizeWheelButton.cleanUp();
            this.view.removePrizeWheelButton();
            this.prizeWheelButton = null;
        }
    }

    public addBotBtn() {
        if (this.botButton) {
            return;
        }
        this.botButton = this.view.addCornerButton('commonui/opt-in_icon', () => {
            HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.BOT);
        });
    }

    public removeBotBtn() {
        if (this.botButton) {
            this.view.removeCornerButton(this.botButton);
            this.botButton = null;
        }
    }

    public handleBotComplete() {
        this.removeBotBtn();
    }

    public addShortcutBtn() {
        if (this.shortcutButton) {
            return;
        }
        this.shortcutButton = this.view.addCornerButton('commonui/opt-in_shortcut_icon', () => {
            HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.SHORTCUT);
        });
    }

    public removeShortcutBtn() {
        if (this.shortcutButton) {
            this.view.removeCornerButton(this.shortcutButton);
            this.shortcutButton = null;
        }
    }

    public handleShortCutComplete() {
        this.removeShortcutBtn();
    }

    public handleJournalClose() {
        const completedMissions = Core.store
            .getState()
            .StateMissions.missions.filter(m => m.missionState === MissionState.COMPLETE);
        const button = this.mapBottomUi ? this.mapBottomUi.getButton(Popup.JOURNAL) : null;
        if (completedMissions.length > 0 && button) {
            button.showNotification();
        }
    }

    public handleSocialClose() {
        this.setSocialNotifications();
    }

    public update() {
        if (this.topUi) {
            this.topUi.update();
        }

        if (this.mapUi) {
            const heartTs = Core.store.getState().StateCurrency.ts;
            if (heartTs) {
                const m = 60 * 1000;
                const timeLeft = m * 30 - (Date.now() - (heartTs - 1));

                if (timeLeft <= 0 && Core.store.getState().StateCurrency.heart < 5) {
                    Core.store.dispatch(Actions.updateHearts(1));
                }

                const minutes = Math.floor(timeLeft / m);
                const minuteStr = minutes >= 10 ? minutes.toString(10) : '0' + minutes.toString(10);
                const seconds = Math.floor((timeLeft - minutes * m) / 1000);
                const secondStr = seconds >= 10 ? seconds.toString(10) : '0' + seconds.toString(10);
                const timeStr = minuteStr + ': ' + secondStr;

                this.mapUi.updateTimer(timeStr);
            } else {
                if (this.mapUi.timeLeft !== 'MAX') {
                    this.mapUi.updateTimer('MAX');
                }
            }
            this.view.update();
        }
    }

    public removeAllCornerButtons() {
        this.view.removeAllCornerButtons();
    }

    public getButtonLoc(config: any) {
        const { id } = config;
        if (this.bottomUi) {
            const btn = this.bottomUi.getButton(id) || this.bottomUi.getButton(BOOSTER_LIST[0]);
            if (btn) {
                const loc = this.view.toLocal(btn.position, btn.parent);
                config = {
                    ...config,
                    ...{
                        loc: new PIXI.Point(loc.x, loc.y),
                    },
                };
            }
        }
        return config;
    }

    public cleanUp() {
        this.topUi = null;
        this.mapUi = null;
        this.bottomUi = null;
        this.mapBottomUi = null;
        super.cleanUp();
    }

    private async addShopGift(shopButton: RoundedIconButton) {
        const { StatePlayer: player } = Core.store.getState();
        let signalGift = false;
        if (Core.sdk.backend && (await Core.sdk.backend.checkConnection())) {
            const now = await Core.sdk.backend.fetchServerTimestamp();
            signalGift = DateUtils.timeRemainingTo(player.shopGift + SHOP_GIFT_TIME, now) <= 0;
        }
        if (!player.shopHasBeenOpened) {
            shopButton.showNotification();
            shopButton.callAttention();
        }
        if (this.mapBottomUi) {
            const sButton = this.mapBottomUi.getButton(Popup.SHOP);
            if (signalGift) {
                sButton.swapButton('mapui/navi-icon-shop-gift');
                this.mapBottomUi.positionButtons();
            }
        }
    }

    private handlePopupClosed(popup: Popup) {
        if (popup === Popup.JOURNAL) {
            this.handleJournalClose();
        } else if (popup === Popup.SOCIAL) {
            this.handleSocialClose();
        } else if (popup === Popup.SHORTCUT) {
            this.handleShortCutComplete();
        } else if (popup === Popup.BOT) {
            this.handleBotComplete();
        }
    }

    private handleScoreChange(state: IGameState, previous: IGameState) {
        if (!this.topUi) {
            return;
        }

        if (state.StateScore.score !== previous.StateScore.score) {
            this.topUi.showScore(state.StateScore.score);
        }
        if (state.StateLevel.goal.total !== previous.StateLevel.goal.total) {
            const remaining: number = state.StateLevel.goal.target - state.StateLevel.goal.total;
            this.topUi.showGoal(remaining);
        }

        if (
            state.StateScore.comboReset !== previous.StateScore.comboReset &&
            state.StateScore.comboReset
        ) {
            this.topUi.showCombo(state.StateScore.multiplier, previous.StateScore.multiplier);
            Core.store.dispatch(Actions.resetComboMeter(false));
        }
    }

    private handlePossibleMovesChange(state: IStateBoard, pre: IStateBoard) {
        if (state.availableMatches.length === pre.availableMatches.length) {
            return;
        }
        this.showPossibleMoves(state.availableMatches);
    }

    private showPossibleMoves(availableMatches: Tile[]) {
        if (this.bottomUi && this.bottomUi.movesCounter) {
            this.bottomUi.movesCounter.showPossibleMoves(availableMatches);
        }
        if (this.topUi && this.topUi.movesCounter) {
            this.topUi.movesCounter.showPossibleMoves(availableMatches);
        }
    }

    private handleCurrencyChange(state: IGameState, previous: IGameState) {
        if (!this.mapUi) {
            return;
        }
        if (state.StateCurrency.heart !== previous.StateCurrency.heart) {
            this.mapUi.updateCurrency('heart', state.StateCurrency.heart);
        }

        const newCoins = state.StateCurrency.coin;
        const oldCoins = previous.StateCurrency.coin;
        if (newCoins < oldCoins) {
            this.mapUi.updateCurrency('coin', newCoins);
        }
    }

    private handleBoosterChange(state: IStateBoosters, previous: IStateBoosters) {
        if (!this.bottomUi) {
            return;
        }
        if (state.availableBoosters !== previous.availableBoosters) {
            this.bottomUi.showBoosters(
                state.availableBoosters,
                Core.store.getState().StateBoosters.freeBoosters
            );
        }

        if (
            state.waitingBoosters !== previous.waitingBoosters ||
            state.activeBoosters !== previous.activeBoosters
        ) {
            const allActive = [...state.waitingBoosters, ...state.activeBoosters];
            BOOSTER_LIST.forEach(boosterId => {
                this.bottomUi.boosters
                    .getButton(boosterId)
                    .setActive(allActive.includes(boosterId), 300);
            });
        }

        if (state.matchCounter !== previous.matchCounter) {
            if (state.activeBoosters.includes(ItemIDs.HIGHLIGHT)) {
                const numLeft = GameItemConfig[ItemIDs.HIGHLIGHT].duration - state.matchCounter;
                this.bottomUi.boosters
                    .getButton(ItemIDs.HIGHLIGHT)
                    .revealActive(numLeft / GameItemConfig[ItemIDs.HIGHLIGHT].duration);
            }
        }
    }

    private handleMissionChange(state: IStateMissions, previous: IStateMissions) {
        if (state.missionsShown && !previous.missionsShown) {
            if (this.mapBottomUi) {
                const button = this.mapBottomUi.getButton(Popup.JOURNAL);
                button.stopCallAttention();
            }
        }
    }

    private handleShopOpened(state: IStatePlayer, previous: IStatePlayer) {
        if (state.shopHasBeenOpened !== previous.shopHasBeenOpened) {
            if (this.mapBottomUi) {
                const shopButton = this.mapBottomUi.getButton(Popup.SHOP);
                if (shopButton) {
                    shopButton.hideNotification();
                    shopButton.stopCallAttention();
                }
            }
        }
    }

    private handleSettingsChange(state: IGameState, previous: IGameState) {
        const { StateSound: stateSound, StateView: stateView } = state;
        const { StateSound: preStateSound, StateView: preStateView } = previous;
        const musicChanged = stateSound.music !== preStateSound.music;
        const sfxChanged = stateSound.soundFx !== preStateSound.soundFx;
        const viewChanged = stateView !== preStateView;

        this.view.updatePauseGroup({
            music: musicChanged ? stateSound.music : null,
            sfx: sfxChanged ? stateSound.soundFx : null,
            view: viewChanged ? stateView : null,
        });
    }

    private setSocialNotifications() {
        const socialState = Core.store.getState().StateSocial;

        if (
            checkForNotification(socialState.sentCoins, COOLDOWN) ||
            checkForNotification(socialState.sentGifts, COOLDOWN) ||
            checkForNotification(socialState.requestedGifts, COOLDOWN)
        ) {
            // should have a better place to do this
            // but the only places I see for resetting is on the buttons themselves, when they are visible
            // if any sent coins have a ts that is passed the timeout, clear it
            const sentCoins = [...socialState.sentCoins];
            const expiredCoins = sentCoins.filter(s => s.ts !== 0 && Date.now() - s.ts > COOLDOWN);
            expiredCoins.forEach(e => {
                Core.store.dispatch(Actions.resetSendCoins(e.friendId));
            });

            const sentGifts = [...socialState.sentGifts];
            const expiredGifts = sentGifts.filter(s => s.ts !== 0 && Date.now() - s.ts > COOLDOWN);
            expiredGifts.forEach(e => {
                Core.store.dispatch(Actions.resetSendGift(e.friendId));
            });

            const requestedGifts = [...socialState.requestedGifts];
            const expiredRequestedGifts = requestedGifts.filter(
                s => s.ts !== 0 && Date.now() - s.ts > COOLDOWN
            );
            expiredRequestedGifts.forEach(e => {
                Core.store.dispatch(Actions.resetRequestGift(e.friendId));
            });

            this.mapBottomUi.getButton(Popup.SOCIAL).showNotification();
        }
    }

    private async handleViewChange(state: IGameState, pre: IGameState) {
        const {
            StatePlayer: player,
            StateSocial: social,
            StateCurrency: currency,
            StateView: stateView,
        } = state;

        const { StateView: preView } = pre;

        if (stateView !== preView) {
            if (preView === Views.MAP) {
                this.removeUi(['map-top', 'map-bottom']);
                this.removeAllCornerButtons();
                this.removePrizeWheelBtn();
            }

            if (stateView === Views.MAP) {
                await this.addMapUi();
                this.addPauseBtn();
                this.showPlayerInfo(player, currency);

                if (social.optInRequired) {
                    this.addBotBtn();
                    Core.store.dispatch(Actions.optInShown());
                }

                if (player.gainedHearts > 0 || player.gainedCoins > 0) {
                    this.mapUi.updateCurrency('coin', currency.coin - player.gainedCoins);
                    this.mapUi.updateCurrency('heart', currency.heart - player.gainedHearts);
                }

                if (player.lastLevelIndex >= 20) {
                    this.addPrizeWheelBtn();
                }

                if (
                    !player.xPromoDismissed &&
                    player.lastLevelIndex >= 20 &&
                    Core.sdk.messages.canSwitchGame()
                ) {
                    this.initXPromo();
                }
            }

            if (preView === Views.GAME) {
                this.removeUi(['game-top', 'game-bottom']);
                // this.removePause();
            }
        }
    }

    private async initXPromo() {
        if (getIsIOS()) {
            return;
        }
        const campaigns = await Core.sdk.messages.getXPromo();
        for (const campaign of campaigns) {
            if (campaign.enabled) {
                this.setupMapXPromo(campaign);
            }
        }
    }

    private setupMapXPromo(campaign: IXPromoConfig) {
        const { assets, gameId, gameName } = campaign;
        const { icon, banner } = assets;
        if (icon && banner) {
            const iconUrl = icon[Core.orientation] || icon[GameOrientation.PORTRAIT];
            const bannerUrl = banner[GameOrientation.LANDSCAPE];
            //@ts-ignore
            const bannerSprite = new EmbeddedSprite({ url: bannerUrl });
            bannerSprite.on(EmbeddedSprite.events.OnComplete, () => {
                if (Core.store.getState().StateView !== Views.MAP) {
                    return;
                }
                //@ts-ignore
                const button = this.view.addCornerButton(iconUrl, () => {
                    XPromoTracking.xPromoShow(gameName);
                    HudEmitter.instance.emit(
                        HudActions.ADD_POPUP,
                        Popup.XPROMO,
                        0,
                        bannerSprite,
                        gameId,
                        gameName
                    );
                    HudEmitter.instance.once(HudActions.CLOSE_POPUP, () => {
                        Core.store.dispatch(Actions.setXPromoDismissed(true));
                        this.view.removeCornerButton(button);
                        XPromoTracking.xPromoClose(gameName);
                    });
                });
            });
        }
    }

    private levelComplete(state: IGameState, previous: IGameState) {
        if (state.StateLevel.complete && !previous.StateLevel.complete) {
            this.removeUi(['game-top', 'game-bottom']);
            this.removePause();
        }
    }
}
